import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Mail, MessageSquare, FileText, Trash2, Check, Plus, Search, X, Copy, ExternalLink, Pencil, Layers,
} from "lucide-react";
import TestDataCleanup from "@/components/TestDataCleanup";
import { normalizeTripDetails, fmtStopTime, fmtBookedOn } from "@/lib/tripDetails";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
function shortAddr(a = "") { return (a || "").split(",")[0] || "—"; }
function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

/** Opens a server-rendered PDF preview (nothing saved) in a new tab. */
async function openPdfPreview(url, payload) {
  try {
    const r = await api.post(url, payload, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(blobUrl, "_blank", "noopener");
  } catch (e) {
    let msg = "Preview failed";
    try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
    toast.error(msg);
  }
}

const STATUS_BADGE = {
  open:            { color: "#A1A1AA", label: "Open" },
  pending_review:  { color: "#F59E0B", label: "Pending review" },
  paid:            { color: "#10B981", label: "Paid" },
};

export default function InvoicesTab() {
  const [tab, setTab] = useState("invoices"); // "invoices" | "new"
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openInv, setOpenInv] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices/consolidated");
      setInvoices(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <section className="pb-20 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex justify-between items-end mb-6">
          <div>
            <h2 className="font-serif text-2xl">Consolidated invoices</h2>
            <p className="text-xs text-[#A1A1AA] mt-1">Bundle multiple past trips into one invoice with an online pay link.</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="tab-inv-list"
              onClick={() => setTab("invoices")}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border transition-colors ${tab === "invoices" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold shadow-[0_0_12px_rgba(212,175,55,0.4)]" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
            >All Invoices ({invoices.length})</button>
            <button
              data-testid="tab-inv-new"
              onClick={() => setTab("new")}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border flex items-center gap-2 transition-colors ${tab === "new" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold shadow-[0_0_12px_rgba(212,175,55,0.4)]" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
            ><Plus size={12}/> New Invoice</button>
            <button
              data-testid="tab-inv-custom"
              onClick={() => setTab("custom")}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border flex items-center gap-2 transition-colors ${tab === "custom" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold shadow-[0_0_12px_rgba(212,175,55,0.4)]" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
            >⚡ Custom Invoice</button>
          </div>
        </div>

        {tab === "invoices" && (
          <InvoiceList
            invoices={invoices}
            loading={loading}
            onOpen={(inv) => setOpenInv(inv)}
            onRefresh={refresh}
          />
        )}
        {tab === "new" && <NewInvoice onCreated={() => { refresh(); setTab("invoices"); }} onSwitchCustom={() => setTab("custom")}/>}
        {tab === "custom" && <CustomInvoice onCreated={(inv) => { refresh(); setTab("invoices"); if (inv?.invoice_id) setOpenInv(inv); }}/>}
      </div>

      {openInv && (
        <InvoiceDetailModal
          invoice_id={openInv.invoice_id}
          onClose={() => setOpenInv(null)}
          onChange={refresh}
        />
      )}
    </section>
  );
}

/* ------------------- List view ------------------- */
function InvoiceList({ invoices, loading, onOpen, onRefresh }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    return (invoices || []).filter(inv => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (!q) return true;
      // Match: invoice #, name, email, phone (digits-only too), AND any of
      // the bundled trip's confirmation codes (e.g. "DLX-F6567D").
      const phone = (inv.customer_phone || "").replace(/\D/g, "");
      const codes = (inv.trip_codes || []).join(" ");
      const t = `${inv.invoice_number} ${inv.customer_name || ""} ${inv.customer_email || ""} ${inv.customer_phone || ""} ${phone} ${codes}`.toLowerCase();
      const qLower = q.toLowerCase();
      // Also let a digits-only phone search (e.g. "4145551234") match a stored phone
      const qDigits = q.replace(/\D/g, "");
      return t.includes(qLower) || (qDigits.length >= 4 && phone.includes(qDigits));
    });
  }, [invoices, q, statusFilter]);

  // Multi-select + bulk void. Paid invoices can't be voided, so they're never selectable.
  const selectable = useMemo(() => filtered.filter(i => i.status !== "paid"), [filtered]);
  const allSelected = selectable.length > 0 && selectable.every(i => selected.has(i.invoice_id));
  const toggleOne = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(i => i.invoice_id)));
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} invoice${ids.length > 1 ? "s" : ""}? Their trips become billable again. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await api.post("/invoices/consolidated/bulk-delete", { invoice_ids: ids });
      const skipped = r.data.skipped_paid?.length || 0;
      toast.success(`${r.data.deleted} invoice${r.data.deleted === 1 ? "" : "s"} deleted${skipped ? ` · ${skipped} paid skipped` : ""}`);
      setSelected(new Set());
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally { setDeleting(false); }
  };

  // Merge ≥2 unpaid trip-invoices of the same client into one combined invoice
  const [merging, setMerging] = useState(false);
  const pickedInvs = useMemo(() => invoices.filter(i => selected.has(i.invoice_id)), [invoices, selected]);
  const pickedClients = new Set(pickedInvs.map(i => (i.customer_email || i.customer_phone || i.customer_name || "").toString().trim().toLowerCase()));
  const canMerge = pickedInvs.length >= 2 && pickedClients.size === 1 && pickedInvs.every(i => i.kind !== "quick" && (i.trip_count || 0) > 0);
  const mergeSelected = async () => {
    const ids = [...selected];
    if (!window.confirm(`Merge ${ids.length} invoices into one? The originals are voided and every trip moves onto the new invoice with a single grand total.`)) return;
    setMerging(true);
    try {
      const r = await api.post("/invoices/consolidated/merge", { invoice_ids: ids });
      toast.success(`Created ${r.data.invoice_number} · ${r.data.trip_count} trips · ${money(r.data.total)}`);
      setSelected(new Set());
      onRefresh?.();
      onOpen?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setMerging(false); }
  };

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]"/>
          <input
            data-testid="inv-search"
            className="field-input pl-9"
            placeholder="Search by trip code (DLX-…), name, email, phone, or invoice #"
            value={q} onChange={e => setQ(e.target.value)}
          />
        </div>
        {["all", "open", "pending_review", "paid"].map(s => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${statusFilter === s ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
          >{s === "all" ? "All" : STATUS_BADGE[s]?.label || s}</button>
        ))}
      </div>

      {loading && <div className="text-xs text-[#A1A1AA]">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center text-sm text-[#A1A1AA]">
          No invoices yet. Click <span className="text-[#D4AF37]">New Invoice</span> to bill a customer for multiple past trips.
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex items-center gap-4 border border-red-400/40 bg-red-500/5 px-4 py-2.5 mb-3" data-testid="inv-bulk-bar">
          <span className="text-xs text-[#F5F2EB]"><span className="text-red-300 font-semibold">{selected.size}</span> selected</span>
          <button data-testid="inv-bulk-clear" onClick={() => setSelected(new Set())} className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-white">Clear</button>
          <button data-testid="inv-bulk-merge" onClick={mergeSelected} disabled={merging || !canMerge}
            title={pickedInvs.length < 2 ? "Select at least two invoices" : pickedClients.size > 1 ? "Selected invoices belong to different clients" : !canMerge ? "Only trip invoices can be merged" : "Combine into one invoice"}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-40 disabled:cursor-not-allowed">
            <Layers size={12}/>{merging ? "Merging…" : `Merge selected into single invoice${selected.size > 1 ? ` (${selected.size})` : ""}`}
          </button>
          <button data-testid="inv-bulk-delete" onClick={bulkDelete} disabled={deleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-red-400/60 text-red-300 hover:bg-red-500/15 disabled:opacity-40">
            <Trash2 size={12}/>{deleting ? "Deleting…" : `Delete selected (${selected.size})`}
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="border border-white/10 bg-[#0A0A0A]" data-testid="inv-list">
          <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5 items-center">
            <div className="col-span-2 flex items-center gap-3">
              <input type="checkbox" data-testid="inv-select-all" checked={allSelected} onChange={toggleAll}
                disabled={selectable.length === 0} title="Select all (unpaid) invoices shown"
                className="accent-[#D4AF37] w-3.5 h-3.5 cursor-pointer disabled:cursor-not-allowed"/>
              <span>Invoice #</span>
            </div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-1 text-right">Trips</div>
            <div className="col-span-2 text-right">Total</div>
            <div className="col-span-2">Issued</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {filtered.map(inv => {
            const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.open;
            const isPaid = inv.status === "paid";
            const checked = selected.has(inv.invoice_id);
            return (
              <div
                key={inv.invoice_id}
                data-testid={`inv-row-${inv.invoice_id}`}
                onClick={() => onOpen(inv)}
                role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === "Enter") onOpen(inv); }}
                className={`w-full grid grid-cols-12 px-4 py-4 border-b border-white/5 last:border-b-0 items-center hover:bg-white/5 text-left text-sm cursor-pointer ${checked ? "bg-[#D4AF37]/5" : ""}`}
              >
                <div className="col-span-2 flex items-center gap-3">
                  <input type="checkbox" data-testid={`inv-check-${inv.invoice_id}`} checked={checked} disabled={isPaid}
                    title={isPaid ? "Paid invoices cannot be deleted" : "Select"}
                    onClick={e => e.stopPropagation()} onChange={() => toggleOne(inv.invoice_id)}
                    className="accent-[#D4AF37] w-3.5 h-3.5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"/>
                  <span className="text-[#D4AF37] font-mono text-xs">{inv.invoice_number}</span>
                </div>
                <div className="col-span-3">
                  <div className="text-[#F5F2EB] text-sm">{inv.customer_name || "—"}</div>
                  <div className="text-[#71717A] text-xs">{inv.customer_email}</div>
                </div>
                <div className="col-span-1 text-right text-[#F5F2EB]">{inv.trip_count}</div>
                <div className="col-span-2 text-right text-[#D4AF37] font-medium">{money(inv.total)}</div>
                <div className="col-span-2 text-[#A1A1AA] text-xs">{fmtDateTime(inv.created_at)}</div>
                <div className="col-span-2 text-right">
                  <span
                    className="px-2 py-1 text-[10px] uppercase tracking-[0.18em]"
                    style={{ background: `${badge.color}20`, color: badge.color, border: `1px solid ${badge.color}55` }}
                  >{badge.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------- New invoice ------------------- */
function NewInvoice({ onCreated, onSwitchCustom }) {
  const [step, setStep] = useState(1); // 1 = pick customer & trips; 2 = config
  const [suggestions, setSuggestions] = useState([]);
  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [discount, setDiscount] = useState(0);
  const [taxPct, setTaxPct] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [markPaid, setMarkPaid] = useState(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [chaufOn, setChaufOn] = useState(false);
  const [chauf, setChauf] = useState({ name: "", phone: "", vehicle: "" });

  // Turning the chauffeur box on pre-fills from the first selected trip
  // that has an assigned driver / vehicle (still fully editable).
  const enableChauffeur = (on) => {
    setChaufOn(on);
    if (on && !chauf.name && !chauf.phone && !chauf.vehicle) {
      const src = trips.find(t => selectedIds.has(t.booking_id) && (t.assigned_driver_name || t.vehicle_name));
      if (src) setChauf({ name: src.assigned_driver_name || "", phone: "", vehicle: src.vehicle_name || "" });
    }
  };

  useEffect(() => {
    api.get("/customers/billing-suggestions").then(r => setSuggestions(r.data || [])).catch(() => {});
  }, []);

  const loadTrips = async (em, nm) => {
    setEmail(em);
    if (nm) setCustomerName(nm);
    setLoadingTrips(true);
    setSelectedIds(new Set());
    try {
      const r = await api.get(`/customers/${encodeURIComponent(em)}/trips?unbilled_only=true`);
      setTrips(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trips");
      setTrips([]);
    } finally {
      setLoadingTrips(false);
    }
  };

  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAll = () => {
    if (selectedIds.size === trips.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(trips.map(t => t.booking_id)));
  };

  const subtotal = useMemo(
    () => trips.filter(t => selectedIds.has(t.booking_id)).reduce((a, t) => a + Number(t.fare || 0), 0),
    [trips, selectedIds]
  );
  const taxAmt = Math.max(0, (subtotal - Number(discount || 0)) * Number(taxPct || 0) / 100);
  const total = Math.max(0, subtotal - Number(discount || 0) + taxAmt);

  const invoicePayload = () => ({
    customer_email: email,
    customer_name: customerName || null,
    trip_ids: Array.from(selectedIds),
    notes: notes || null,
    due_date: dueDate || null,
    discount: Number(discount) || 0,
    tax_pct: Number(taxPct) || 0,
    mark_paid: markPaid,
    payment_method: markPaid ? payMethod : null,
    paid_date: markPaid ? new Date().toISOString().slice(0, 10) : null,
    chauffeur: chaufOn ? {
      name: chauf.name.trim() || null,
      phone: chauf.phone.trim() || null,
      vehicle: chauf.vehicle.trim() || null,
    } : null,
  });

  const previewPdf = async () => {
    if (!email || selectedIds.size === 0) {
      toast.error("Pick a customer and at least one trip");
      return;
    }
    setBusy(true);
    await openPdfPreview("/invoices/consolidated/preview-pdf", invoicePayload());
    setBusy(false);
  };

  const create = async () => {
    if (!email || selectedIds.size === 0) {
      toast.error("Pick a customer and at least one trip");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/invoices/consolidated", invoicePayload());
      toast.success(`Invoice ${r.data.invoice_number} created`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-invoice">
      {/* Step 1: Customer + trips */}
      <div className="border border-white/10 bg-[#0A0A0A] p-5">
        <div className="overline mb-3">1 · Customer</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="field-label">Customer email</label>
            <input
              data-testid="cust-email"
              className="field-input" placeholder="customer@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onBlur={() => email && loadTrips(email)}
            />
          </div>
          <div>
            <label className="field-label">Customer name (optional)</label>
            <input
              data-testid="cust-name"
              className="field-input" placeholder="ACME Corp / John Doe"
              value={customerName} onChange={e => setCustomerName(e.target.value)}
            />
          </div>
        </div>

        {suggestions.length > 0 && !email && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Customers with unbilled trips</div>
              <TestDataCleanup onDone={() => window.location.reload()}/>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map(c => (
                <button
                  key={c.email}
                  data-testid={`suggest-${c.email}`}
                  onClick={() => loadTrips(c.email, c.name)}
                  className="text-xs px-3 py-2 border border-white/10 hover:border-[#D4AF37] flex items-center gap-2"
                >
                  <span className="text-[#F5F2EB]">{c.name || c.email}</span>
                  <span className="text-[#D4AF37]">{c.trip_count} trips · {money(c.total_fare)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Trips */}
      {email && (
        <div className="border border-white/10 bg-[#0A0A0A]">
          <div className="px-5 py-3 border-b border-white/5 flex justify-between items-center">
            <div>
              <div className="overline">2 · Trips to include</div>
              <div className="text-[11px] text-[#71717A]">{trips.length} unbilled trip{trips.length === 1 ? "" : "s"} found</div>
            </div>
            {trips.length > 0 && (
              <button
                data-testid="select-all"
                onClick={toggleAll}
                className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]"
              >{selectedIds.size === trips.length ? "Clear all" : "Select all"}</button>
            )}
          </div>
          {loadingTrips && <div className="p-6 text-xs text-[#A1A1AA]">Loading trips…</div>}
          {!loadingTrips && trips.length === 0 && (
            <div className="p-6 text-xs text-[#A1A1AA]">
              No unbilled trips for this customer.
              <button data-testid="switch-to-custom" onClick={onSwitchCustom}
                className="block mt-2 text-[#D4AF37] uppercase tracking-[0.18em] text-[10px] hover:underline">
                ⚡ Create a custom invoice instead — type your own line items (e.g. a charter not booked in the system) →
              </button>
            </div>
          )}
          {trips.map(t => {
            const checked = selectedIds.has(t.booking_id);
            return (
              <label
                key={t.booking_id}
                data-testid={`trip-${t.booking_id}`}
                className={`grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/5 last:border-b-0 items-center cursor-pointer ${checked ? "bg-[#D4AF37]/5" : "hover:bg-white/5"}`}
              >
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(t.booking_id)}
                    className="accent-[#D4AF37]"
                  />
                </div>
                <div className="col-span-3 text-xs text-[#F5F2EB]">{fmtDateTime(t.pickup_datetime)}</div>
                <div className="col-span-5 flex items-center gap-2 text-sm">
                  <span className="text-[#F5F2EB] truncate">{shortAddr(t.pickup_address)}</span>
                  <ArrowRight size={12} className="text-[#D4AF37] shrink-0"/>
                  <span className="text-[#F5F2EB] truncate">{shortAddr(t.dropoff_address)}</span>
                </div>
                <div className="col-span-2 text-[#A1A1AA] text-xs">{t.vehicle_name || "—"}</div>
                <div className="col-span-1 text-right text-[#D4AF37] font-medium">{money(t.fare)}</div>
              </label>
            );
          })}
        </div>
      )}

      {/* Step 3: Totals + config */}
      {selectedIds.size > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
          <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
            <div className="overline">3 · Invoice details</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="field-label">Discount ($)</label>
                <input data-testid="discount" type="number" min="0" step="0.01"
                  className="field-input" value={discount} onChange={e => setDiscount(e.target.value)}/>
              </div>
              <div>
                <label className="field-label">Tax (%)</label>
                <input data-testid="tax-pct" type="number" min="0" step="0.01"
                  className="field-input" value={taxPct} onChange={e => setTaxPct(e.target.value)}/>
              </div>
              <div>
                <label className="field-label">Due date</label>
                <input data-testid="due-date" type="date"
                  className="field-input" value={dueDate} onChange={e => setDueDate(e.target.value)}/>
              </div>
            </div>
            <div>
              <label className="field-label">Notes (optional, shown on invoice)</label>
              <textarea
                data-testid="notes"
                className="field-input min-h-[80px]" rows={3}
                value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Net 15 — bill to ACME Accounts Payable"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#F5F2EB]">
              <input data-testid="mark-paid" type="checkbox"
                checked={markPaid} onChange={e => setMarkPaid(e.target.checked)}
                className="accent-[#D4AF37]"/>
              Already paid — mark as paid on create
            </label>
            {markPaid && (
              <select
                data-testid="pay-method"
                className="field-input" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                <option value="cash">Cash</option>
                <option value="zelle">Zelle</option>
                <option value="venmo">Venmo</option>
                <option value="card">Card (offline)</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            )}
          </div>
          <ChauffeurFields on={chaufOn} setOn={enableChauffeur} val={chauf} setVal={setChauf} idPrefix="ni"/>
          </div>

          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 space-y-3">
            <div className="overline">Summary</div>
            <Row k="Trips" v={String(selectedIds.size)}/>
            <Row k="Subtotal" v={money(subtotal)}/>
            {Number(discount) > 0 && <Row k="Discount" v={`−${money(discount)}`}/>}
            {Number(taxPct) > 0  && <Row k={`Tax (${Number(taxPct).toFixed(2)}%)`} v={money(taxAmt)}/>}
            <div className="border-t border-[#D4AF37]/40 pt-3 flex justify-between items-baseline">
              <span className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">Total</span>
              <span className="text-2xl text-[#D4AF37] font-semibold">{money(total)}</span>
            </div>
            <button
              data-testid="preview-invoice-pdf-btn"
              disabled={busy || selectedIds.size === 0}
              onClick={previewPdf}
              className="btn-ghost w-full justify-center"
            ><FileText size={13}/> Preview PDF first</button>
            <button
              data-testid="create-invoice-btn"
              disabled={busy || selectedIds.size === 0}
              onClick={create}
              className="btn-gold w-full"
            >{busy ? "Creating…" : "Create invoice"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------- Detail modal ------------------- */
function InvoiceDetailModal({ invoice_id, onClose, onChange }) {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [payEditing, setPayEditing] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "zelle", date: "" });

  const load = async () => {
    try {
      const r = await api.get(`/invoices/consolidated/${invoice_id}`);
      setInv(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoice_id]);

  const openEdit = () => {
    const trips = normalizeTripDetails(inv.trip_details).map(td => ({
      pickup: td.pickup_address || "",
      dropoff: td.dropoff_address || "",
      when: td.pickup_datetime || "",
      bookedOn: td.booked_on || "",
      tripType: td.trip_type === "hourly" ? "hourly" : "point_to_point",
      hours: td.hours ? String(td.hours) : "",
      stops: (td.stops || []).map(s => ({ address: s.address || "", time: s.time || "" })),
      // preserved as-is (multi-trip quick charges): chauffeurs, per-trip amount / description
      passengers: td.passengers ?? null, chauffeurs: td.chauffeurs || null, amount: td.amount ?? null, description: td.description || null, label: td.label || null,
    }));
    setEdit({
      customer_email: inv.customer_email || "",
      customer_phone: inv.customer_phone || "",
      customer_name: inv.customer_name || "",
      due_date: inv.due_date || "",
      notes: inv.notes || "",
      discount: inv.discount || 0,
      tax_pct: inv.tax_pct || 0,
      chauf_name: inv.chauffeur?.name || "",
      chauf_phone: inv.chauffeur?.phone || "",
      chauf_vehicle: inv.chauffeur?.vehicle || "",
      trips: trips.length ? trips : [{ tripType: "point_to_point", hours: "", pickup: "", dropoff: "", when: "", bookedOn: "", stops: [] }],
    });
    setEditing(true);
  };
  const setEditTrip = (i, k, v) =>
    setEdit(ed => ({ ...ed, trips: ed.trips.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const cancelEdit = () => { setEditing(false); setEdit({}); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/invoices/consolidated/${invoice_id}`, {
        customer_email: edit.customer_email || null,
        customer_phone: edit.customer_phone || null,
        customer_name: edit.customer_name || null,
        due_date: edit.due_date || "",
        notes: edit.notes || "",
        discount: Number(edit.discount) || 0,
        tax_pct: Number(edit.tax_pct) || 0,
        chauffeur: {
          name: (edit.chauf_name || "").trim() || null,
          phone: (edit.chauf_phone || "").trim() || null,
          vehicle: (edit.chauf_vehicle || "").trim() || null,
        },
        ...(inv.kind === "quick" ? { trips:
          (edit.trips || [])
            .map(t => ({
              pickup_address: t.pickup.trim() || null,
              dropoff_address: t.dropoff.trim() || null,
              stops: t.stops
                .map(s => ({ address: (s.address || "").trim() || null, time: (s.time || "").trim() || null }))
                .filter(s => s.address || s.time),
              pickup_datetime: t.when.trim() || null,
              booked_on: t.bookedOn.trim() || null,
              trip_type: t.tripType || "point_to_point",
              hours: t.tripType === "hourly" && Number(t.hours) > 0 ? Number(t.hours) : null,
              passengers: t.passengers ?? null, chauffeurs: t.chauffeurs || null, amount: t.amount ?? null, description: t.description || null, label: t.label || null,
            }))
            .filter(t => t.pickup_address || t.dropoff_address || t.stops.length || t.pickup_datetime || t.booked_on),
        } : {}),
      });
      setInv(r.data);
      setEditing(false);
      toast.success("Invoice updated");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/invoices/consolidated/${invoice_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv?.invoice_number || "invoice"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/consolidated/${invoice_id}/email`);
      if (r.data.status === "MOCKED")
        toast.success("Email logged (Resend not live — MOCKED).");
      else
        toast.success("Invoice emailed to customer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setBusy(false);
    }
  };

  const sendSms = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/consolidated/${invoice_id}/send-sms`);
      toast.success(`Pay link texted to ${r.data.to || "customer"}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "SMS failed";
      if (msg.includes("test_number")) toast.warning("Not sent — 555 test numbers are blocked to avoid SMS charges");
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    const method = window.prompt("Payment method (cash, zelle, venmo, card, check, other):", "zelle");
    if (!method) return;
    setBusy(true);
    try {
      await api.post(`/invoices/consolidated/${invoice_id}/pay`, { payment_method: method });
      toast.success("Marked as paid");
      load(); onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const openPayEdit = () => {
    setPayForm({
      amount: String(inv.amount_received ?? inv.total ?? ""),
      method: inv.payment_method || "zelle",
      date: inv.paid_date || new Date().toISOString().slice(0, 10),
    });
    setPayEditing(true);
  };
  const savePayEdit = async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter the amount received"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/invoices/consolidated/${invoice_id}/payment`, {
        amount_received: amt,
        payment_method: payForm.method,
        paid_date: payForm.date || null,
      });
      setInv(r.data);
      setPayEditing(false);
      toast.success("Payment updated");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update payment");
    } finally {
      setBusy(false);
    }
  };

  const voidInv = async () => {
    if (!window.confirm("Void this invoice? The trips will become billable again.")) return;
    setBusy(true);
    try {
      await api.delete(`/invoices/consolidated/${invoice_id}`);
      toast.success("Invoice voided");
      onClose(); onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (!inv?.payment_url) return;
    navigator.clipboard.writeText(inv.payment_url);
    toast.success("Pay link copied");
  };

  if (!inv) return null;
  const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.open;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="inv-detail-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full my-auto">
        <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Invoice</div>
            <div className="font-serif text-2xl text-[#D4AF37] mt-1">{inv.invoice_number}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">{inv.customer_name || inv.customer_email}</div>
            {(() => {
              const tripCodes = (inv.trips || []).map(t => t.confirmation_code).filter(Boolean);
              const codes = tripCodes.length ? tripCodes : (inv.confirmation_code ? [inv.confirmation_code] : []);
              if (codes.length === 0) return null;
              return (
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]/80 mt-1.5" data-testid="invoice-reservations">
                  Reservation{codes.length === 1 ? "" : "s"}: {codes.join(" · ")}
                </div>
              );
            })()}
            {inv.created_at && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1" data-testid="invoice-booked-on">
                Booked on: {new Date(inv.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago", timeZoneName: "short" })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="px-2 py-1 text-[10px] uppercase tracking-[0.18em]"
              style={{ background: `${badge.color}20`, color: badge.color, border: `1px solid ${badge.color}55` }}
            >{badge.label}</span>
            <button data-testid="close-modal" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Pay link */}
          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
            <div className="overline text-[#A1A1AA] mb-2">Online payment link</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-[#F5F2EB] break-all">{inv.payment_url}</code>
              <button
                data-testid="copy-link"
                onClick={copyLink}
                className="btn-ghost !py-2 !px-3 text-[10px]"
              ><Copy size={12}/> Copy</button>
              <button
                data-testid="sms-link"
                onClick={sendSms}
                disabled={busy}
                title={inv.customer_phone ? `Text the pay link to ${inv.customer_phone}` : "No phone on this invoice — add one via Modify"}
                className="btn-ghost !py-2 !px-3 text-[10px] disabled:opacity-40"
              ><MessageSquare size={12}/> {busy ? "Sending…" : "Text link"}</button>
              <a
                href={inv.payment_url}
                target="_blank" rel="noreferrer"
                data-testid="open-link"
                className="btn-ghost !py-2 !px-3 text-[10px] no-underline"
              ><ExternalLink size={12}/> Open</a>
            </div>
            <div className="text-[10px] text-[#71717A] mt-2 uppercase tracking-[0.18em]">
              Customer can pay Card · Apple Pay · Google Pay · Zelle · Venmo
            </div>
          </div>

          {/* Edit panel — only visible while editing an OPEN invoice */}
          {editing && (
            <div className="border border-[#3B82F6]/40 bg-[#3B82F6]/5 p-4 space-y-3" data-testid="inv-edit-panel">
              <div className="overline text-[#3B82F6]">Modify invoice</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Customer name</label>
                  <input data-testid="edit-name" className="field-input"
                    value={edit.customer_name || ""}
                    onChange={e => setEdit({ ...edit, customer_name: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label">Customer email</label>
                  <input data-testid="edit-email" type="email" className="field-input"
                    value={edit.customer_email || ""}
                    onChange={e => setEdit({ ...edit, customer_email: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label">Customer phone</label>
                  <input data-testid="edit-phone" className="field-input"
                    value={edit.customer_phone || ""}
                    onChange={e => setEdit({ ...edit, customer_phone: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label">Due date</label>
                  <input data-testid="edit-due" type="date" className="field-input"
                    value={edit.due_date || ""}
                    onChange={e => setEdit({ ...edit, due_date: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label">Discount ($)</label>
                  <input data-testid="edit-discount" type="number" min="0" step="0.01" className="field-input"
                    value={edit.discount ?? 0}
                    onChange={e => setEdit({ ...edit, discount: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label">Tax (%)</label>
                  <input data-testid="edit-tax" type="number" min="0" step="0.01" className="field-input"
                    value={edit.tax_pct ?? 0}
                    onChange={e => setEdit({ ...edit, tax_pct: e.target.value })}/>
                </div>
              </div>
              <div>
                <label className="field-label">Notes</label>
                <textarea data-testid="edit-notes" rows={2} className="field-input min-h-[60px]"
                  value={edit.notes || ""}
                  onChange={e => setEdit({ ...edit, notes: e.target.value })}/>
              </div>
              <div className="border-t border-white/10 pt-3 space-y-2" data-testid="edit-chauffeur-section">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">Chauffeur info <span className="text-[#71717A] normal-case tracking-normal">(optional — shown on invoice, pay page &amp; PDF)</span></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="field-label">Chauffeur name</label>
                    <input data-testid="edit-chauffeur-name" className="field-input" placeholder="e.g. Marcus J."
                      value={edit.chauf_name || ""}
                      onChange={e => setEdit({ ...edit, chauf_name: e.target.value })}/>
                  </div>
                  <div>
                    <label className="field-label">Chauffeur phone</label>
                    <input data-testid="edit-chauffeur-phone" className="field-input" placeholder="(414) 555-0123"
                      value={edit.chauf_phone || ""}
                      onChange={e => setEdit({ ...edit, chauf_phone: e.target.value })}/>
                  </div>
                  <div>
                    <label className="field-label">Vehicle details</label>
                    <input data-testid="edit-chauffeur-vehicle" className="field-input" placeholder="e.g. Black Escalade · ABC-123"
                      value={edit.chauf_vehicle || ""}
                      onChange={e => setEdit({ ...edit, chauf_vehicle: e.target.value })}/>
                  </div>
                </div>
              </div>
              {inv.kind === "quick" && (edit.trips || []).map((trip, ti) => (
                <div key={ti} className="border-t border-white/10 pt-3 space-y-2" data-testid={`edit-trip-section-${ti}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">
                      {(edit.trips || []).length > 1 ? `Trip ${ti + 1}` : "Trip route"}
                    </div>
                    {(edit.trips || []).length > 1 && (
                      <button data-testid={`edit-trip-remove-${ti}`}
                        onClick={() => setEdit(ed => ({ ...ed, trips: ed.trips.filter((_, idx) => idx !== ti) }))}
                        className="text-[#71717A] hover:text-red-400 flex items-center gap-1 text-[10px] uppercase tracking-[0.18em]">
                        <Trash2 size={12}/> Remove trip
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-end gap-4">
                    <TripTypeSelector idPrefix="edit" ti={ti} value={trip.tripType || "point_to_point"} onChange={v => setEditTrip(ti, "tripType", v)}/>
                    {trip.tripType === "hourly" && (
                      <div>
                        <label className="field-label">Duration (hours)</label>
                        <input data-testid={`edit-hours-${ti}`} type="number" min="1" step="0.5" className="field-input" style={{ width: "9rem" }}
                          value={trip.hours || ""} onChange={e => setEditTrip(ti, "hours", e.target.value)}/>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="field-label">{trip.tripType === "hourly" ? "Starting address" : "Pickup address"}</label>
                      <input data-testid={`edit-pickup-${ti}`} className="field-input"
                        value={trip.pickup}
                        onChange={e => setEditTrip(ti, "pickup", e.target.value)}/>
                    </div>
                    <div>
                      <label className="field-label">Trip date &amp; time</label>
                      <input data-testid={`edit-trip-when-${ti}`} type="datetime-local" className="field-input [color-scheme:dark]"
                        value={trip.when}
                        onChange={e => setEditTrip(ti, "when", e.target.value)}/>
                    </div>
                    <div>
                      <label className="field-label">Trip booked on</label>
                      <input data-testid={`edit-booked-on-${ti}`} type="date" className="field-input [color-scheme:dark]"
                        value={trip.bookedOn}
                        onChange={e => setEditTrip(ti, "bookedOn", e.target.value)}/>
                    </div>
                  </div>
                  {trip.tripType !== "hourly" && trip.stops.map((st, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] w-14 shrink-0">Stop {i + 1}</span>
                      <input data-testid={`edit-stop-${ti}-${i}`} className="field-input flex-1" value={st.address}
                        onChange={e => setEditTrip(ti, "stops", trip.stops.map((s, idx) => idx === i ? { ...s, address: e.target.value } : s))}/>
                      <input data-testid={`edit-stop-time-${ti}-${i}`} type="time" className="field-input [color-scheme:dark]"
                        style={{ width: "8rem", flex: "0 0 auto" }}
                        title="Pickup time at this stop (optional)"
                        value={st.time}
                        onChange={e => setEditTrip(ti, "stops", trip.stops.map((s, idx) => idx === i ? { ...s, time: e.target.value } : s))}/>
                      <button data-testid={`edit-stop-remove-${ti}-${i}`}
                        onClick={() => setEditTrip(ti, "stops", trip.stops.filter((_, idx) => idx !== i))}
                        className="text-[#71717A] hover:text-red-400 p-1.5"><Trash2 size={14}/></button>
                    </div>
                  ))}
                  {trip.tripType !== "hourly" && (
                    <>
                      <button data-testid={`edit-add-stop-${ti}`}
                        onClick={() => setEditTrip(ti, "stops", [...trip.stops, { address: "", time: "" }])}
                        className="btn-ghost !py-1.5 !px-3 text-[10px]"><Plus size={12}/> Add stop</button>
                      <div>
                        <label className="field-label">Drop-off address</label>
                        <input data-testid={`edit-dropoff-${ti}`} className="field-input"
                          value={trip.dropoff}
                          onChange={e => setEditTrip(ti, "dropoff", e.target.value)}/>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {inv.kind === "quick" && (
                <button data-testid="edit-add-trip"
                  onClick={() => setEdit(ed => ({ ...ed, trips: [...(ed.trips || []), { tripType: "point_to_point", hours: "", pickup: "", dropoff: "", when: "", bookedOn: "", stops: [] }] }))}
                  className="btn-ghost !py-1.5 !px-3 text-[10px]"><Plus size={12}/> Add another trip</button>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button data-testid="edit-cancel" onClick={cancelEdit} disabled={busy}
                  className="btn-ghost text-[11px]">Cancel</button>
                <button data-testid="edit-save" onClick={saveEdit} disabled={busy}
                  className="btn-gold text-[11px]">{busy ? "Saving…" : "Save changes"}</button>
              </div>
              <div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">
                Subtotal is fixed by the bundled trips. Adjusting discount or tax % automatically recomputes the total.
              </div>
            </div>
          )}

          {/* Trips — or custom line items for quick invoices */}
          {(inv.trips || []).length === 0 && (inv.items || []).length > 0 ? (
            <div className="border border-white/10" data-testid="modal-items">
              {inv.description && (
                <div className="px-3 py-2 border-b border-white/5 text-xs text-[#D4AF37]">{inv.description}</div>
              )}
              {normalizeTripDetails(inv.trip_details).map((td, ti, all) => (
                <div key={ti} className="px-3 py-3 border-b border-white/5 text-xs space-y-1.5" data-testid={`modal-trip-details-${ti}`}>
                  {all.length > 1 && (
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{td.label || `Trip ${ti + 1}`}</div>
                  )}
                  {td.trip_type && (
                    <div className="text-[#F5F2EB]" data-testid={`inv-trip-type-${ti}`}><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Type</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5">{td.trip_type === "hourly" ? "Hourly Charter" : "Point to Point"}</span>
                      {td.trip_type === "hourly" && td.hours ? <span className="ml-2 text-[#A1A1AA]">{td.hours} hour{td.hours === 1 ? "" : "s"}</span> : null}
                    </div>
                  )}
                  {td.booked_on && (
                    <div className="text-[#A1A1AA]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Booked</span>{fmtBookedOn(td.booked_on)}</div>
                  )}
                  {td.pickup_datetime && (
                    <div className="text-[#A1A1AA]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">When</span>{td.pickup_datetime.replace("T", " · ")}</div>
                  )}
                  {td.pickup_address && (
                    <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">{td.trip_type === "hourly" ? "Start" : "Pickup"}</span>{td.pickup_address}</div>
                  )}
                  {(td.stops || []).map((st, i) => (
                    <div key={i} className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] inline-block w-16">Stop {i + 1}</span>{st.time ? `${fmtStopTime(st.time)} — ` : ""}{st.address}</div>
                  ))}
                  {td.dropoff_address && (
                    <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Drop-off</span>{td.dropoff_address}</div>
                  )}
                  {td.passengers > 0 && (
                    <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Pax</span>{td.passengers} passenger{td.passengers === 1 ? "" : "s"}</div>
                  )}
                </div>
              ))}
              <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5">
                <div className="col-span-9">Description</div>
                <div className="col-span-3 text-right">Amount</div>
              </div>
              {inv.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 px-3 py-3 border-b border-white/5 last:border-b-0 items-center text-sm" data-testid="modal-item-row">
                  <div className="col-span-9 text-[#F5F2EB] text-xs">{it.description}</div>
                  <div className="col-span-3 text-right text-[#D4AF37]">{money(it.amount)}</div>
                </div>
              ))}
            </div>
          ) : (
          <div className="border border-white/10">
            <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5">
              <div className="col-span-3">Date / Time</div>
              <div className="col-span-6">Route</div>
              <div className="col-span-1 text-right">Pax</div>
              <div className="col-span-2 text-right">Fare</div>
            </div>
            {inv.trips.map(t => (
              <div key={t.booking_id} className="grid grid-cols-12 px-3 py-3 border-b border-white/5 last:border-b-0 items-center text-sm" data-testid="modal-trip-row">
                <div className="col-span-3 text-[#F5F2EB] text-xs">{fmtDateTime(t.pickup_datetime)}</div>
                <div className="col-span-6 flex items-center gap-2 text-xs">
                  <span className="text-[#F5F2EB] truncate">{shortAddr(t.pickup_address)}</span>
                  <ArrowRight size={12} className="text-[#D4AF37] shrink-0"/>
                  <span className="text-[#F5F2EB] truncate">{shortAddr(t.dropoff_address)}</span>
                </div>
                <div className="col-span-1 text-right text-[#A1A1AA] text-xs">{t.pax || t.passengers || "—"}</div>
                <div className="col-span-2 text-right text-[#D4AF37]">{money(t.fare)}</div>
              </div>
            ))}
          </div>
          )}
          {/* Chauffeur block — shown when set on the invoice */}
          {inv.chauffeur && (inv.chauffeur.name || inv.chauffeur.phone || inv.chauffeur.vehicle) && !editing && (
            <div className="border border-white/10 p-3 text-xs space-y-1.5" data-testid="modal-chauffeur">
              <div className="overline text-[#71717A] mb-1">Chauffeur</div>
              {inv.chauffeur.name && (
                <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Name</span>{inv.chauffeur.name}</div>
              )}
              {inv.chauffeur.phone && (
                <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Phone</span>{inv.chauffeur.phone}</div>
              )}
              {inv.chauffeur.vehicle && (
                <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Vehicle</span>{inv.chauffeur.vehicle}</div>
              )}
            </div>
          )}
          {/* Totals */}
          <div className="ml-auto max-w-xs">
            <Row k="Subtotal" v={money(inv.subtotal)}/>
            {inv.discount > 0 && <Row k="Discount" v={`−${money(inv.discount)}`}/>}
            {inv.tax_pct > 0  && <Row k={`Tax (${inv.tax_pct.toFixed(2)}%)`} v={money(inv.tax)}/>}
            <div className="border-t border-[#D4AF37]/40 mt-2 pt-2 flex justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">Total</span>
              <span className="text-xl text-[#D4AF37] font-semibold">{money(inv.total)}</span>
            </div>
          </div>

          {/* Payment record — actual amount received (may differ from total, e.g. Zelle w/ tip) */}
          {inv.status === "paid" && !payEditing && (
            <div className="border border-[#10B981]/30 bg-[#10B981]/5 p-3 text-sm" data-testid="payment-record">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="overline text-[#10B981] mb-1">Payment received</div>
                  <div className="text-[#F5F2EB]">
                    {money(inv.amount_received ?? inv.total)}
                    <span className="text-[#A1A1AA]"> via {inv.payment_method || "—"}{inv.paid_date ? ` on ${inv.paid_date}` : ""}</span>
                  </div>
                  {Number(inv.amount_received) > 0 && Math.abs(inv.amount_received - inv.total) >= 0.01 && (
                    <div className={`text-xs mt-1 ${inv.amount_received > inv.total ? "text-[#10B981]" : "text-[#F87171]"}`}>
                      {inv.amount_received > inv.total
                        ? `+${money(inv.amount_received - inv.total)} above invoice total`
                        : `${money(inv.total - inv.amount_received)} short of invoice total`}
                    </div>
                  )}
                </div>
                <button data-testid="edit-payment-btn" disabled={busy} onClick={openPayEdit} className="btn-ghost text-[11px]">
                  <Pencil size={12}/> Edit payment
                </button>
              </div>
            </div>
          )}
          {payEditing && (
            <div className="border border-[#D4AF37]/40 p-3" data-testid="payment-edit-form">
              <div className="overline text-[#D4AF37] mb-2">Edit payment received</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#71717A]">Amount received ($)</label>
                  <input data-testid="pay-amount-input" type="number" step="0.01" min="0"
                    value={payForm.amount}
                    onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#71717A]">Method</label>
                  <select data-testid="pay-method-select" value={payForm.method}
                    onChange={e => setPayForm(p => ({ ...p, method: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none">
                    {["zelle", "venmo", "cash", "card", "check", "paypal", "other"].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#71717A]">Date</label>
                  <input data-testid="pay-date-input" type="date" value={payForm.date}
                    onChange={e => setPayForm(p => ({ ...p, date: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"/>
                </div>
              </div>
              <div className="flex gap-2 justify-end mt-3">
                <button disabled={busy} onClick={() => setPayEditing(false)} className="btn-ghost text-[11px]">Cancel</button>
                <button data-testid="save-payment-btn" disabled={busy} onClick={savePayEdit} className="btn-gold text-[11px]">
                  <Check size={12}/> Save payment
                </button>
              </div>
            </div>
          )}

          {inv.notes && (
            <div className="border border-white/10 p-3 text-xs text-[#A1A1AA]">
              <div className="overline text-[#71717A] mb-1">Notes</div>
              {inv.notes}
            </div>
          )}
        </div>

        <div className="border-t border-white/5 px-6 py-4 flex flex-wrap gap-2 justify-end">
          <button data-testid="dl-pdf" disabled={busy} onClick={downloadPdf} className="btn-ghost text-[11px]">
            <FileText size={12}/> Download PDF
          </button>
          {inv.status !== "paid" && !editing && (
            <button data-testid="edit-inv" disabled={busy} onClick={openEdit} className="btn-ghost text-[11px]">
              <Pencil size={12}/> Modify
            </button>
          )}
          {inv.customer_email && (
            <button data-testid="send-email" disabled={busy || editing} onClick={sendEmail} className="btn-ghost text-[11px]">
              <Mail size={12}/> Send Email
            </button>
          )}
          {inv.customer_phone && (
            <button data-testid="send-sms" disabled={busy || editing} onClick={sendSms} className="btn-ghost text-[11px]">
              <MessageSquare size={12}/> Send SMS
            </button>
          )}
          {inv.status !== "paid" && (
            <button data-testid="mark-paid-btn" disabled={busy || editing} onClick={markPaid} className="btn-gold text-[11px]">
              <Check size={12}/> Mark paid
            </button>
          )}
          {inv.status !== "paid" && (
            <button data-testid="void-inv" disabled={busy || editing} onClick={voidInv} className="btn-ghost text-[11px] text-red-400">
              <Trash2 size={12}/> Void
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------- Custom invoice (own line items, no booking needed) ------------------- */
function CustomInvoice({ onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  // Multiple trips per invoice (Trip 1, Trip 2, …). Each stop: {address, time}.
  const emptyTrip = () => ({ tripType: "point_to_point", hours: "", pickup: "", dropoff: "", when: "", bookedOn: "", stops: [] });
  const [tripsArr, setTripsArr] = useState([emptyTrip()]);
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [notes, setNotes] = useState("");
  const [markPaid, setMarkPaid] = useState(false);
  const [payMethod, setPayMethod] = useState("zelle");
  const [busy, setBusy] = useState(false);
  // Invoice-from-history: search past trips and pre-fill the whole form
  const [tripQ, setTripQ] = useState("");
  const [tripResults, setTripResults] = useState([]);
  // Optional chauffeur block shown on the invoice / pay page / PDF
  const [chaufOn, setChaufOn] = useState(false);
  const [chauf, setChauf] = useState({ name: "", phone: "", vehicle: "" });

  useEffect(() => {
    if (tripQ.trim().length < 2) { setTripResults([]); return; }
    const t = setTimeout(() => {
      api.get("/bookings/find", { params: { q: tripQ.trim() } })
        .then(r => setTripResults(r.data || []))
        .catch(() => setTripResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [tripQ]);

  const tripHasData = (t) => !!(
    t.pickup.trim() || t.dropoff.trim() || t.when.trim() || t.bookedOn.trim() ||
    t.stops.some(s => (s.address || "").trim() || (s.time || "").trim())
  );
  const setTripField = (i, k, v) =>
    setTripsArr(arr => arr.map((t, idx) => idx === i ? { ...t, [k]: v } : t));

  const applyTrip = (b) => {
    const fromBooking = {
      tripType: b.trip_type === "hourly" ? "hourly" : "point_to_point",
      hours: b.trip_type === "hourly" && b.hours ? String(b.hours) : "",
      pickup: b.pickup_address || "",
      dropoff: b.trip_type === "hourly" ? "" : (b.dropoff_address || ""),
      when: (b.pickup_datetime || "").slice(0, 16),
      bookedOn: (b.created_at || "").slice(0, 10),
      stops: (b.stops || []).map(s => ({ address: s, time: "" })),
    };
    const lineItem = {
      description: `${b.vehicle_name ? b.vehicle_name + " — " : ""}${b.pickup_address || "charter"} to ${b.dropoff_address || ""}`.trim(),
      amount: String(b.fare ?? ""),
    };
    const appending = tripsArr.some(tripHasData);
    if (appending) {
      // Form already has a trip — add this one as another trip on the same invoice
      setTripsArr(arr => [...arr, fromBooking]);
      setItems(arr => {
        const kept = arr.filter(it => it.description.trim() || Number(it.amount) > 0);
        return [...kept, lineItem];
      });
      toast.success(`Added ${b.confirmation_code} as Trip ${tripsArr.length + 1}`);
    } else {
      setName(b.customer_name || "");
      setEmail(b.customer_email || "");
      setPhone(b.customer_phone || "");
      setTripsArr([fromBooking]);
      const when = (b.pickup_datetime || "").slice(0, 10);
      setDescription(`Trip ${b.confirmation_code || ""}${when ? ` · ${when}` : ""}`.trim());
      setItems([lineItem]);
      setMarkPaid((b.payment_status || "") === "paid");
      toast.success(`Pre-filled from ${b.confirmation_code} — adjust anything, then preview or create`);
    }
    if (b.assigned_driver_name || b.vehicle_name) {
      setChauf(c => (c.name || c.phone || c.vehicle) ? c : { name: b.assigned_driver_name || "", phone: "", vehicle: b.vehicle_name || "" });
      setChaufOn(true);
    }
    setTripQ("");
    setTripResults([]);
  };

  const setItem = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems(arr => [...arr, { description: "", amount: "" }]);
  const removeItem = (i) => setItems(arr => arr.filter((_, idx) => idx !== i));
  const total = items.reduce((a, it) => a + (Number(it.amount) || 0), 0);

  const validateAndBuild = () => {
    if (!name.trim()) { toast.error("Customer name is required"); return null; }
    const clean = items
      .map(it => ({ description: it.description.trim(), amount: Number(it.amount) || 0 }))
      .filter(it => it.description && it.amount > 0);
    if (clean.length === 0) { toast.error("Add at least one line item with a description and amount"); return null; }
    return {
      customer_name: name.trim(),
      customer_email: email.trim() || null,
      customer_phone: phone.trim() || null,
      description: description.trim() || clean[0].description,
      items: clean,
      notes: notes.trim() || null,
      trips: (() => {
        const clean = tripsArr.filter(tripHasData).map(t => ({
          trip_type: t.tripType || "point_to_point",
          hours: t.tripType === "hourly" && Number(t.hours) > 0 ? Number(t.hours) : null,
          pickup_address: t.pickup.trim() || null,
          dropoff_address: t.tripType === "hourly" ? null : (t.dropoff.trim() || null),
          stops: t.stops
            .map(s => ({ address: (s.address || "").trim() || null, time: (s.time || "").trim() || null }))
            .filter(s => s.address || s.time),
          pickup_datetime: t.when.trim() || null,
          booked_on: t.bookedOn.trim() || null,
        }));
        return clean.length ? clean : null;
      })(),
      chauffeur: chaufOn ? {
        name: chauf.name.trim() || null,
        phone: chauf.phone.trim() || null,
        vehicle: chauf.vehicle.trim() || null,
      } : null,
      mark_paid: markPaid,
      payment_method: markPaid ? payMethod : null,
      paid_date: markPaid ? new Date().toISOString().slice(0, 10) : null,
    };
  };

  const previewPdf = async () => {
    const payload = validateAndBuild();
    if (!payload) return;
    setBusy(true);
    await openPdfPreview("/invoices/quick/preview-pdf", payload);
    setBusy(false);
  };

  const create = async () => {
    if (!email.trim() && !phone.trim()) return toast.error("Provide an email or phone so the customer can receive the invoice");
    const payload = validateAndBuild();
    if (!payload) return;
    setBusy(true);
    try {
      const r = await api.post("/invoices/quick", payload);
      toast.success(`Invoice ${r.data.invoice_number} created — ${money(r.data.total)}`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="custom-invoice">
      {/* ── Start from a past trip (optional) ── */}
      <div className="border border-white/10 bg-[#0A0A0A] p-5 relative" data-testid="ci-history-card">
        <div className="overline mb-1">Start from a past trip <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(optional — pre-fills everything)</span></div>
        <div className="relative mt-2">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]"/>
          <input data-testid="ci-trip-search" className="field-input pl-9"
            placeholder="Search by confirmation code, name, email or phone…"
            value={tripQ} onChange={e => setTripQ(e.target.value)}/>
        </div>
        {tripResults.length > 0 && (
          <div className="absolute left-5 right-5 z-20 bg-[#111] border border-[#D4AF37]/40 max-h-64 overflow-y-auto shadow-xl" data-testid="ci-trip-results">
            {tripResults.map(b => (
              <button key={b.booking_id} data-testid={`ci-trip-pick-${b.booking_id}`} onClick={() => applyTrip(b)}
                className="w-full text-left px-3 py-2.5 border-b border-white/5 last:border-b-0 hover:bg-[#D4AF37]/10 transition-colors">
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-[#F5F2EB] truncate">{b.customer_name} <span className="text-[#D4AF37] font-mono text-xs ml-1">{b.confirmation_code}</span></span>
                  <span className="text-[#D4AF37] shrink-0">{money(b.fare)}</span>
                </div>
                <div className="text-[10px] text-[#71717A] truncate mt-0.5">
                  {(b.pickup_datetime || "").slice(0, 10)} · {b.pickup_address} → {b.dropoff_address}
                  {(b.stops || []).length > 0 && ` · ${b.stops.length} stop${b.stops.length === 1 ? "" : "s"}`}
                  {b.payment_status === "paid" && " · PAID"}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-5">
        <div className="overline mb-1">1 · Customer</div>
        <p className="text-[11px] text-[#71717A] mb-4">For charters and trips not booked in the system — type your own line items and send a pay link.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="field-label">Customer name *</label>
            <input data-testid="ci-name" className="field-input" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Email</label>
            <input data-testid="ci-email" className="field-input" placeholder="customer@example.com" value={email} onChange={e => setEmail(e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Phone</label>
            <input data-testid="ci-phone" className="field-input" placeholder="(414) 555-0123" value={phone} onChange={e => setPhone(e.target.value)}/>
          </div>
        </div>
        <div className="mt-3">
          <label className="field-label">Invoice title (shown on the invoice & pay page)</label>
          <input data-testid="ci-description" className="field-input"
            placeholder="e.g. Private charter — Oconomowoc / Waukesha / Big Bend / Wind Lake"
            value={description} onChange={e => setDescription(e.target.value)}/>
        </div>
      </div>

      {tripsArr.map((trip, ti) => (
        <div key={ti} className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`ci-trip-card-${ti}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="overline">
              2 · {tripsArr.length > 1 ? `Trip ${ti + 1}` : "Trip route"}
              <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(optional — shown on the invoice)</span>
            </div>
            {tripsArr.length > 1 && (
              <button data-testid={`ci-trip-remove-${ti}`}
                onClick={() => setTripsArr(arr => arr.filter((_, idx) => idx !== ti))}
                className="text-[#71717A] hover:text-red-400 flex items-center gap-1 text-[10px] uppercase tracking-[0.18em]">
                <Trash2 size={12}/> Remove trip
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <TripTypeSelector idPrefix="ci" ti={ti} value={trip.tripType || "point_to_point"} onChange={v => setTripField(ti, "tripType", v)}/>
            {trip.tripType === "hourly" && (
              <div>
                <label className="field-label">Duration (hours)</label>
                <input data-testid={`ci-hours-${ti}`} type="number" min="1" step="0.5" className="field-input" style={{ width: "9rem" }} placeholder="e.g. 3"
                  value={trip.hours} onChange={e => setTripField(ti, "hours", e.target.value)}/>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="field-label">{trip.tripType === "hourly" ? "Starting address" : "Pickup address"}</label>
              <input data-testid={`ci-pickup-${ti}`} className="field-input" placeholder={trip.tripType === "hourly" ? "Where the charter begins" : "e.g. Oconomowoc, WI"}
                value={trip.pickup} onChange={e => setTripField(ti, "pickup", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Trip date &amp; time</label>
              <input data-testid={`ci-trip-when-${ti}`} type="datetime-local" className="field-input [color-scheme:dark]"
                value={trip.when} onChange={e => setTripField(ti, "when", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Trip booked on <span className="text-[#71717A] normal-case">(shown on PDF)</span></label>
              <input data-testid={`ci-booked-on-${ti}`} type="date" className="field-input [color-scheme:dark]"
                value={trip.bookedOn} onChange={e => setTripField(ti, "bookedOn", e.target.value)}/>
            </div>
          </div>
          {trip.tripType !== "hourly" && trip.stops.map((st, i) => (
            <div key={i} className="flex gap-2 items-center mt-3" data-testid={`ci-stop-${ti}-${i}`}>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] w-14 shrink-0">Stop {i + 1}</span>
              <input data-testid={`ci-stop-input-${ti}-${i}`} className="field-input flex-1" placeholder="e.g. Waukesha, WI (dinner)"
                value={st.address}
                onChange={e => setTripField(ti, "stops", trip.stops.map((s, idx) => idx === i ? { ...s, address: e.target.value } : s))}/>
              <input data-testid={`ci-stop-time-${ti}-${i}`} type="time" className="field-input [color-scheme:dark]"
                style={{ width: "8rem", flex: "0 0 auto" }}
                title="Pickup time at this stop (optional)"
                value={st.time}
                onChange={e => setTripField(ti, "stops", trip.stops.map((s, idx) => idx === i ? { ...s, time: e.target.value } : s))}/>
              <button data-testid={`ci-stop-remove-${ti}-${i}`}
                onClick={() => setTripField(ti, "stops", trip.stops.filter((_, idx) => idx !== i))}
                className="text-[#71717A] hover:text-red-400 p-1.5"><Trash2 size={14}/></button>
            </div>
          ))}
          {trip.tripType !== "hourly" && (
            <>
              <button data-testid={`ci-add-stop-${ti}`}
                onClick={() => setTripField(ti, "stops", [...trip.stops, { address: "", time: "" }])}
                className="btn-ghost !py-2 !px-3 text-[10px] mt-3"><Plus size={12}/> Add stop</button>
              <div className="mt-3">
                <label className="field-label">Drop-off address</label>
                <input data-testid={`ci-dropoff-${ti}`} className="field-input" placeholder="e.g. Oconomowoc, WI (return)"
                  value={trip.dropoff} onChange={e => setTripField(ti, "dropoff", e.target.value)}/>
              </div>
            </>
          )}
        </div>
      ))}
      <button data-testid="ci-add-trip" onClick={() => setTripsArr(arr => [...arr, emptyTrip()])}
        className="btn-ghost !py-2.5 !px-4 text-[11px] w-full justify-center border-dashed">
        <Plus size={13}/> Add another trip — same client, same invoice (Trip {tripsArr.length + 1})
      </button>

      <ChauffeurFields on={chaufOn} setOn={setChaufOn} val={chauf} setVal={setChauf} idPrefix="ci"/>

      <div className="border border-white/10 bg-[#0A0A0A] p-5">
        <div className="overline mb-3">3 · Line items</div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`ci-item-${i}`}>
              <input
                data-testid={`ci-item-desc-${i}`}
                className="field-input flex-1"
                placeholder={i === 0 ? "e.g. 5:15 PM Oconomowoc → Waukesha (dinner) → Big Bend → Wind Lake event → 11:15 PM return" : "Description"}
                value={it.description} onChange={e => setItem(i, "description", e.target.value)}
              />
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] text-sm">$</span>
                <input
                  data-testid={`ci-item-amount-${i}`}
                  type="number" min="0" step="0.01" placeholder="0.00"
                  className="field-input pl-7 text-right"
                  value={it.amount} onChange={e => setItem(i, "amount", e.target.value)}
                />
              </div>
              {items.length > 1 && (
                <button data-testid={`ci-item-remove-${i}`} onClick={() => removeItem(i)}
                  className="text-[#71717A] hover:text-red-400 p-1.5"><Trash2 size={14}/></button>
              )}
            </div>
          ))}
        </div>
        <button data-testid="ci-add-item" onClick={addItem} className="btn-ghost !py-2 !px-3 text-[10px] mt-3">
          <Plus size={12}/> Add line
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
          <div className="overline">4 · Details</div>
          <div>
            <label className="field-label">Notes (optional, shown on invoice)</label>
            <textarea data-testid="ci-notes" className="field-input min-h-[70px]" rows={2}
              value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Thank you for choosing Delux Limo"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#F5F2EB]">
            <input data-testid="ci-mark-paid" type="checkbox" checked={markPaid}
              onChange={e => setMarkPaid(e.target.checked)} className="accent-[#D4AF37]"/>
            Already paid — mark as paid on create
          </label>
          {markPaid && (
            <select data-testid="ci-pay-method" className="field-input" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
              {["zelle", "venmo", "cash", "card", "check", "other"].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
        </div>
        <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 space-y-3">
          <div className="overline">Summary</div>
          <Row k="Line items" v={String(items.filter(it => Number(it.amount) > 0).length)}/>
          <div className="border-t border-[#D4AF37]/40 pt-3 flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">Total</span>
            <span className="text-2xl text-[#D4AF37] font-semibold" data-testid="ci-total">{money(total)}</span>
          </div>
          <button data-testid="ci-preview-pdf-btn" disabled={busy} onClick={previewPdf} className="btn-ghost w-full justify-center">
            <FileText size={13}/> Preview PDF first
          </button>
          <button data-testid="ci-create-btn" disabled={busy} onClick={create} className="btn-gold w-full">
            {busy ? "Creating…" : "Create invoice"}
          </button>
          <div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">Preview opens in a new tab — check it, adjust, then create</div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-[#A1A1AA] text-xs">{k}</span>
      <span className="text-[#F5F2EB]">{v}</span>
    </div>
  );
}

/* Shared "Include chauffeur info" checkbox + editable fields (create forms). */
function ChauffeurFields({ on, setOn, val, setVal, idPrefix }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`${idPrefix}-chauffeur-card`}>
      <label className="flex items-center gap-2 text-sm text-[#F5F2EB] cursor-pointer">
        <input data-testid={`${idPrefix}-chauffeur-toggle`} type="checkbox" checked={on}
          onChange={e => setOn(e.target.checked)} className="accent-[#D4AF37]"/>
        Include chauffeur info
        <span className="text-[#71717A] text-[10px] uppercase tracking-[0.14em] ml-1">shown on the invoice, pay page &amp; PDF</span>
      </label>
      {on && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="field-label">Chauffeur name</label>
            <input data-testid={`${idPrefix}-chauffeur-name`} className="field-input" placeholder="e.g. Marcus J."
              value={val.name} onChange={e => setVal(v => ({ ...v, name: e.target.value }))}/>
          </div>
          <div>
            <label className="field-label">Chauffeur phone</label>
            <input data-testid={`${idPrefix}-chauffeur-phone`} className="field-input" placeholder="(414) 555-0123"
              value={val.phone} onChange={e => setVal(v => ({ ...v, phone: e.target.value }))}/>
          </div>
          <div>
            <label className="field-label">Vehicle details</label>
            <input data-testid={`${idPrefix}-chauffeur-vehicle`} className="field-input" placeholder="e.g. Black Cadillac Escalade · Plate ABC-123"
              value={val.vehicle} onChange={e => setVal(v => ({ ...v, vehicle: e.target.value }))}/>
          </div>
        </div>
      )}
    </div>
  );
}


/** Trip type toggle for custom-invoice trip cards (create + edit). */
function TripTypeSelector({ value, onChange, idPrefix, ti }) {
  const opts = [["point_to_point", "Point to Point"], ["hourly", "Hourly Charter"]];
  return (
    <div className="flex border border-white/15 w-fit" data-testid={`${idPrefix}-trip-type-${ti}`} role="radiogroup">
      {opts.map(([k, label]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k}
          data-testid={`${idPrefix}-trip-type-${k}-${ti}`}
          onClick={() => onChange(k)}
          className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${value === k ? "bg-[#D4AF37] text-[#050505]" : "text-[#A1A1AA] hover:text-[#F5F2EB]"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}
